import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Form, Button, Collapse, Card, FilterGrid, FilterField } from '@/components/ui';
import reportService from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import ExportOptionsModal from '../components/ExportOptionsModal';
import Toast from '../../../components/Toast';
import { ServiceArea } from '../../../services/areasService';
import { employeeOptionName, useEmployeeOptions } from '../utils/reportFilterOptions';

type PayrollVariancePreviewRow = {
  id: string;
  'Employee Number': string;
  'Employee Name': string;
  'Work Date': string;
  'Task Index': string;
  Field: string;
  'Old Value': string;
  'New Value': string;
  'Changed By': string;
  'Changed At': string;
  'First Payroll Export Date': string;
  'Post-Payment': string;
  Stage: string;
  'Service Area': string;
};

const PREVIEW_COLUMNS = [
  { label: 'Employee #', key: 'Employee Number', sortable: false, width: '90px' },
  { label: 'Employee Name', key: 'Employee Name', sortable: false, width: '140px' },
  { label: 'Work Date', key: 'Work Date', sortable: false, width: '95px' },
  { label: 'Timecard entry', key: 'Task Index', sortable: false, width: '110px' },
  { label: 'Field', key: 'Field', sortable: false, width: '120px' },
  { label: 'Old Value', key: 'Old Value', sortable: false, width: '100px' },
  { label: 'New Value', key: 'New Value', sortable: false, width: '100px' },
  { label: 'Changed By', key: 'Changed By', sortable: false, width: '110px' },
  { label: 'Changed At', key: 'Changed At', sortable: false, width: '150px' },
  { label: 'First Paid', key: 'First Payroll Export Date', sortable: false, width: '150px' },
  {
    label: 'Post-Pay',
    key: 'Post-Payment',
    sortable: false,
    width: '70px',
    headerTooltip:
      'Y = change after the form’s first payroll export (First Paid).\nN = change was not after first export.'
  },
  {
    label: 'Stage',
    key: 'Stage',
    sortable: false,
    width: '70px',
    headerTooltip:
      'updated = field edit on the daily log.\nderived = system-computed payroll field (e.g. Department, Finance Class, Foreman Area).'
  },
  { label: 'SA', key: 'Service Area', sortable: false, width: '50px' }
];

interface PayrollVarianceReportViewProps {
  serviceAreas: ServiceArea[];
  canCreatePayrollVariance: boolean;
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const PayrollVarianceReportView: React.FC<PayrollVarianceReportViewProps> = ({
  serviceAreas,
  canCreatePayrollVariance,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    serviceArea: defaultServiceAreaId || '',
    employeeName: '',
    exportStatus: 'unexported' as 'unexported' | 'exported' | 'all',
    postPayment: 'yes' as 'yes' | 'no' | 'all'
  });
  const [previewData, setPreviewData] = useState<PayrollVariancePreviewRow[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    onPageChange: (page: number, pageSize?: number) => {
      void page;
      void pageSize;
    }
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingPreliminary, setLoadingPreliminary] = useState(false);
  const [loadingFinal, setLoadingFinal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const employeeOptions = useEmployeeOptions(filters.serviceArea);

  useEffect(() => {
    if (defaultServiceAreaId && !filters.serviceArea) {
      setFilters((f) => ({ ...f, serviceArea: defaultServiceAreaId }));
    }
  }, [defaultServiceAreaId, filters.serviceArea]);

  // Clear employee selection when it falls outside the selected service area.
  useEffect(() => {
    setFilters((f) => {
      const employeeValid =
        !f.employeeName || employeeOptions.some((emp) => employeeOptionName(emp) === f.employeeName);
      if (employeeValid) return f;
      return { ...f, employeeName: '' };
    });
  }, [employeeOptions]);

  const handlePageChange = (page: number, pageSize?: number) => {
    void handleGeneratePreview(page, pageSize);
  };

  const handleGeneratePreview = useCallback(async (page: number = 1, pageSize?: number) => {
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const res = await reportService.getPayrollVariancePreview({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        serviceArea: filters.serviceArea || undefined,
        employeeName: filters.employeeName || undefined,
        exportStatus: filters.exportStatus,
        postPayment: filters.postPayment,
        page,
        limit: pageSize || pagination.limit
      });
      const limit = res.pagination.limit;
      const pageNum = res.pagination.page;
      const rows: PayrollVariancePreviewRow[] = (res.data as Record<string, string>[]).map((row, i) => ({
        id: `${pageNum}-${i}`,
        'Employee Number': String(row['Employee Number'] ?? ''),
        'Employee Name': String(row['Employee Name'] ?? ''),
        'Work Date': String(row['Work Date'] ?? ''),
        'Task Index': String(row['Task Index'] ?? ''),
        Field: String(row.Field ?? ''),
        'Old Value': String(row['Old Value'] ?? ''),
        'New Value': String(row['New Value'] ?? ''),
        'Changed By': String(row['Changed By'] ?? ''),
        'Changed At': String(row['Changed At'] ?? ''),
        'First Payroll Export Date': String(row['First Payroll Export Date'] ?? ''),
        'Post-Payment': String(row['Post-Payment'] ?? ''),
        Stage: String(row.Stage ?? ''),
        'Service Area': String(row['Service Area'] ?? '')
      }));
      setPreviewData(rows);
      setPagination({
        page: res.pagination.page,
        limit,
        total: res.pagination.total,
        totalPages: res.pagination.totalPages,
        onPageChange: handlePageChange
      });
    } catch {
      setErrorMessage('Failed to load payroll variance preview.');
      setPreviewData([]);
    } finally {
      setLoadingPreview(false);
    }
  }, [filters, pagination.limit]);

  const exportReport = async (dryRun: boolean) => {
    if (!canCreatePayrollVariance) return;
    setErrorMessage(null);
    try {
      await reportService.exportPayrollVarianceReport({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        serviceArea: filters.serviceArea || undefined,
        employeeName: filters.employeeName || undefined,
        exportStatus: filters.exportStatus,
        postPayment: filters.postPayment,
        dryRun
      });
      setToastMessage(
        dryRun
          ? 'Preliminary report downloaded (variances not marked exported).'
          : 'Payroll variance exported.'
      );
      setShowToast(true);
      setShowExportModal(false);
      await handleGeneratePreview(pagination.page, pagination.limit);
    } catch {
      setErrorMessage('Failed to export payroll variance report.');
    }
  };

  const handlePreliminaryExport = async () => {
    setLoadingPreliminary(true);
    try {
      await exportReport(true);
    } finally {
      setLoadingPreliminary(false);
    }
  };

  const handleFinalizedExport = async () => {
    setLoadingFinal(true);
    try {
      await exportReport(false);
    } finally {
      setLoadingFinal(false);
    }
  };

  const tableColumns = PREVIEW_COLUMNS.map((c) => ({
    ...c,
    render: (row: PayrollVariancePreviewRow) => {
      const value = String(row[c.key as keyof PayrollVariancePreviewRow] ?? '');
      if (c.key === 'Post-Payment' && value === 'Y') {
        return <span className="tw-font-bold tw-text-danger">{value}</span>;
      }
      return value;
    }
  }));

  const handlePageSizeChange = (newPageSize: number) => {
    void handleGeneratePreview(1, newPageSize);
  };

  return (
    <div className="tw-flex tw-flex-col" style={{ height: '100%', minHeight: 0 }}>
      <h3 className="tw-mb-3">Payroll Variance Report</h3>
      <p className="tw-text-content-muted tw-text-sm tw-mb-3">
        Shows field-level edits on forms that have been included in a weekly payroll export. Rows flagged
        Post-Pay (Y) were changed after the form was first paid.
      </p>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

      <ReportParametersCard
        onGenerate={() => void handleGeneratePreview(1)}
        onExport={() => {}}
        loadingGenerate={loadingPreview}
        loadingExport={false}
        disableExport={true}
        generateButtonText="Run Report"
        hideExportButton={true}
        defaultCollapsed={false}
        onCollapseChange={setIsFiltersCollapsed}
      >
        <FilterGrid min="lg">
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
            <Form.Select
              size="sm"
              value={filters.serviceArea}
              onChange={(e) => setFilters((f) => ({ ...f, serviceArea: e.target.value }))}
              disabled={loadingServiceAreas}
            >
              <option value="">All</option>
              {serviceAreas.map((a) => (
                <option key={a.id} value={String(a.id)}>
                  {a.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <FilterField span={2}>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">
                Change date range{' '}
                <span className="tw-font-normal tw-text-content-muted">
                  (Filters by when the edit was recorded, not the work date on the form.)
                </span>
              </Form.Label>
              <div className="tw-flex tw-items-center tw-gap-2">
                <Form.Control
                  size="sm"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
                  style={{ width: '140px' }}
                />
                <span className="tw-text-content-muted tw-text-sm">to</span>
                <Form.Control
                  size="sm"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
                  style={{ width: '140px' }}
                />
              </div>
            </Form.Group>
          </FilterField>
        </FilterGrid>

        <div className="tw-mt-3">
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
            className="tw-p-0 tw-no-underline"
            style={{ fontSize: '0.875rem' }}
          >
            <i className={`bi bi-chevron-${showAdditionalFilters ? 'up' : 'down'} tw-mr-1`}></i>
            Additional Filters
          </Button>
          <Collapse in={showAdditionalFilters}>
            <div>
              <FilterGrid className="tw-mt-2">
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Export status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.exportStatus}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        exportStatus: e.target.value as 'unexported' | 'exported' | 'all'
                      }))
                    }
                  >
                    <option value="unexported">Unexported</option>
                    <option value="exported">Exported</option>
                    <option value="all">All</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Post-pay</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.postPayment}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        postPayment: e.target.value as 'yes' | 'no' | 'all'
                      }))
                    }
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="all">All</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Employee name</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.employeeName}
                    onChange={(e) => setFilters((f) => ({ ...f, employeeName: e.target.value }))}
                  >
                    <option value="">All</option>
                    {employeeOptions.map((emp) => (
                      <option key={emp.id} value={employeeOptionName(emp)}>
                        {employeeOptionName(emp)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </FilterGrid>
            </div>
          </Collapse>
        </div>
      </ReportParametersCard>

      <div className="tw-grow tw-mt-2" style={{ minHeight: 0, minWidth: 0, overflow: 'auto' }}>
        <Card style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center">
            <h6 className="tw-mb-0">Report Data ({pagination.total} records)</h6>
            {pagination.total > 0 && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowExportModal(true)}
                disabled={!canCreatePayrollVariance}
              >
                <i className="bi bi-download tw-mr-1"></i>
                Export Report
              </Button>
            )}
          </Card.Header>
          <Card.Body className="tw-p-0" style={{ flex: 1, minHeight: 0 }}>
            <ReportPreviewTable<PayrollVariancePreviewRow>
              columns={tableColumns}
              data={previewData}
              loading={loadingPreview}
              yellowHeaderColumn="Employee Name"
              pagination={{
                page: pagination.page,
                limit: pagination.limit,
                total: pagination.total,
                totalPages: pagination.totalPages,
                onPageChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange
              }}
              isFiltersCollapsed={isFiltersCollapsed}
              emptyMessage="No data available. Click 'Run Report' to load data."
            />
          </Card.Body>
        </Card>
      </div>

      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onPreliminaryExport={() => void handlePreliminaryExport()}
        onFinalExport={() => void handleFinalizedExport()}
        loadingPreliminary={loadingPreliminary}
        loadingFinal={loadingFinal}
        isPayroll={true}
        size="small"
      />
      <Toast type="success" message={toastMessage} show={showToast} onClose={() => setShowToast(false)} />
    </div>
  );
};

export default PayrollVarianceReportView;
